import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Mail, Lock, Loader2, Sparkles, AlertCircle } from 'lucide-react';
import { API_URL } from '../../config';
import EmailVerifyForm from './EmailVerifyForm';

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null); // non-null => compte pas encore verifie

  const attemptLogin = async () => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await attemptLogin();

      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_type', data.user_type);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_name', data.name);
        onLogin(data);
      } else if (data.error_code === 'email_not_verified') {
        setPendingUserId(data.user_id);
      } else {
        setError(data.message || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Impossible de se connecter au serveur');
    }

    setLoading(false);
  };

  const handleVerified = async () => {
    try {
      const data = await attemptLogin();
      if (data.success) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_type', data.user_type);
        localStorage.setItem('user_id', data.user_id);
        localStorage.setItem('user_name', data.name);
        onLogin(data);
      } else {
        setPendingUserId(null);
        setError(data.message || 'Erreur de connexion');
      }
    } catch (err) {
      setPendingUserId(null);
      setError('Impossible de se connecter au serveur');
    }
  };

  const RobotVideo = () => (
    <div className="relative w-40 h-40 mx-auto">
      {/* Aura lumineuse */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
      
      {/* Vidéo en cercle */}
      <div className="relative z-10 w-40 h-40 rounded-full overflow-hidden border-4 border-gradient-to-br from-blue-500 via-purple-500 to-cyan-500 shadow-2xl">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="/Teckia-face.mp4" type="video/mp4" />
        </video>
      </div>
      
      {/* Cercles d'énergie animés */}
      <div className="absolute inset-0 border-4 border-blue-500/30 rounded-full animate-ping" style={{animationDuration: '2s'}}></div>
      <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full animate-ping" style={{animationDuration: '3s', animationDelay: '1s'}}></div>
      
      {/* Particles flottantes autour */}
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-blue-400/50 rounded-full animate-float"
          style={{
            top: `${Math.random() * 150}%`,
            left: `${Math.random() * 150}%`,
            animationDelay: `${i * 0.3}s`,
            animationDuration: `${3 + Math.random() * 2}s`
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animations de fond */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 animate-float">
          <Brain className="h-16 w-16 text-blue-400/20" />
        </div>
        <div className="absolute bottom-20 right-20 animate-float-delayed">
          <Sparkles className="h-12 w-12 text-purple-400/20" />
        </div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
        <RobotVideo />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
            Agent TeckiA
          </h1>
          <p className="text-blue-200">Agent Intelligent Polyvalent</p>
        </div>

        {/* Formulaire */}
        <div className="bg-black/40 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 shadow-2xl">
          {pendingUserId ? (
            <EmailVerifyForm
              userId={pendingUserId}
              email={email}
              onVerified={handleVerified}
              onCancel={() => setPendingUserId(null)}
            />
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email ou téléphone
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="votre@email.com ou 034 12 345 67"
                required
              />
            </div>

            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="••••••••"
                required
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-4 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3 shadow-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>
          )}

          {!pendingUserId && (
          <div className="text-center mt-6">
            <span className="text-blue-200/60 text-sm">Pas encore de compte ? </span>
            <button
              onClick={() => navigate('/signup')}
              className="text-blue-300 hover:text-blue-200 text-sm font-semibold transition-colors"
            >
              Créer un compte gratuit
            </button>
          </div>
          )}
        </div>
        {/* Liens légaux */}
        <div className="text-center mt-6 flex items-center justify-center gap-6">
          <a
            href="/privacy"
            className="text-blue-300/50 hover:text-blue-300 text-xs transition-colors"
          >
            Politique de Confidentialité
          </a>
          <span className="text-white/20 text-xs">·</span>
          <a
            href="/terms"
            className="text-blue-300/50 hover:text-blue-300 text-xs transition-colors"
          >
            CGU
          </a>
        </div>
      </div>
      
    </div>
  );
}